import { Form, Input, InputNumber, Select, Switch } from 'antd';
import { useEffect, useState } from 'react';
import {
  getAdminServiceList,
  postServiceConfig,
  putServiceConfig,
} from '../../../api';
import { IJsonEditorError } from '../../../components/json-editor/model';
import {
  CommonStatusStep,
  IServiceConfigItem,
  IServiceConfigUpdateParam,
} from '../../../model/api-modules';
import { IPageServiceConfigListItem, ISelectOptionItem } from '.';
import { ReactJsonEditor } from '../../../components/react-json-editor';
import { messageError, messageSuccess } from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';
import { getConfig } from '../../../config';
import {
  getExtraManageSites,
  getFirstManageSite,
  getNeedManageSites,
  getSiteText,
  strToJson,
} from '../../../lib';
import { omit } from 'lodash';
import { wrapRequestArea } from '../../../lib/http/lib';
import { getServiceConfigOptions } from './lib';
import styles from './index.module.scss';
import { MultiSiteStatus } from '../components/multi-site-status';
import { ConsoleForm } from '../../../components/form';
import { ModalWrap } from '../../../components/modal-wrap';
import { MainButton } from '../../../components/button/main';
import { CommonSelect } from '../../../components/select';
import { getValueText } from '../../../lib/options';
import { shortHash10 } from '../lib/hash';

interface IProps {
  dialogMode: 'view' | 'edit' | 'add';
  info?: IPageServiceConfigListItem;
  userLists: ISelectOptionItem[];
  onClose: () => void;
  onChange: () => void;
}

export interface IPageFormServiceConfig
  extends Omit<
    IServiceConfigItem,
    'Pic' | 'JsonConfig' | 'DataSource' | 'DataSyncID'
  > {
  Pic: string[];
  JsonConfig?: Record<string, any>;
  ServicePathSplitBySite?: boolean;
  SiteStatus: Record<string, CommonStatusStep>;
  [key: string]: any;
}

export const AddService = (props: IProps) => {
  const { onClose, info, dialogMode } = props;
  const [creating, setCreating] = useState<boolean>(false);

  const [hasError, setHasError] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<IPageFormServiceConfig>();

  // 创建表单
  const [form] = Form.useForm<IPageFormServiceConfig>();

  const isViewMode = dialogMode === 'view';

  useEffect(() => {
    if (!info) {
      form.setFieldsValue({ JsonConfig: {} });
      return;
    }
    const servicePathsMap = getNeedManageSites().reduce<Record<string, string>>(
      (acc, site) => {
        if (site === getFirstManageSite()) {
          acc[`${getFirstManageSite()}ServicePath`] = info.ServicePath;
          return acc;
        }
        acc[`${site}ServicePath`] =
          info.allSiteServiceItems?.find((item) => item.site === site)
            ?.ServicePath || '';
        return acc;
      },
      {}
    );
    const initValues = {
      ...info,
      Pic: info.PicUserIds,
      JsonConfig: strToJson<Record<string, any>>(info.JsonConfig) || {},
      ServicePathSplitBySite: info.allSiteServiceItems?.some(
        (item) => item.ServicePath !== info.ServicePath
      ),
      ...servicePathsMap,
      SiteStatus: getSiteStatus(),
    };
    form.setFieldsValue(initValues);
    setFormValues(initValues);
  }, [info]);

  const getSiteStatus = () => {
    if (!info) {
      return {};
    }
    return getNeedManageSites().reduce<Record<string, CommonStatusStep>>(
      (acc, site) => {
        acc[site] =
          info.allSiteServiceItems?.find((item) => item.site === site)
            ?.Status || CommonStatusStep.Pending;
        return acc;
      },
      {}
    );
  };

  // 创建服务
  const createServiceMethods = async (value: IServiceConfigItem) => {
    const dataSyncID = await shortHash10(value.UniqueName);
    const res = await postServiceConfig({
      ...value,
      DataSyncID: dataSyncID,
      DataSource: 'support',
    });
    if (res) {
      messageSuccess(getFormatMessage('service_create_success'));
      await getAdminServiceList();
      onClose();
      return true;
    }
    messageError(getFormatMessage('service_create_fail'));
    return false;
  };

  const getSiteServicePath = (site: string, values: IPageFormServiceConfig) => {
    if (!values.ServicePathSplitBySite) {
      return decodeURIComponent(values.ServicePath || '');
    }

    return decodeURIComponent(values[`${site}ServicePath`] || '');
  };

  // 提交创建表单
  const onSubmitForm = async () => {
    if (hasError) {
      console.error(getFormatMessage('jsonconfig_not_valid'));
      return false;
    }
    const values = await form.validateFields();

    const trimmedName = values.Name?.trim();
    const trimmedSubServiceNames = values.SubServiceNames
      ? values.SubServiceNames.trim()
      : '';
    const updatedValues = {
      ...omit(values, [
        'ServicePathSplitBySite',
        'SiteStatus',
        ...getNeedManageSites().map((site) => `${site}ServicePath`),
      ]),

      Name: trimmedName,
      UniqueName: values.UniqueName,
      JsonConfig: values.JsonConfig,
      SubServiceNames: trimmedSubServiceNames,
      ParentSort:
        getServiceConfigOptions().ParentName.findIndex(
          (item) => item.label === values.ParentName
        ) + 1,
    };

    const jsonString = JSON.stringify(updatedValues.JsonConfig);

    const params = {
      ...updatedValues,
      JsonConfig: jsonString,
      Pic: form.getFieldValue('Pic')?.toString() || '',
    };

    const woaParams = {
      ...params,
      ServicePath: getSiteServicePath(getFirstManageSite(), values),
      Status: values.SiteStatus[getFirstManageSite()],
    };
    setCreating(true);

    const woaRes = await submitToSite(woaParams, getFirstManageSite());
    const otherRes = await Promise.all(
      getExtraManageSites().map((site) => {
        const siteParams = {
          ...params,
          ServicePath: getSiteServicePath(site, values),
          Status: values.SiteStatus[site],
        };
        return submitToSite(siteParams, site);
      })
    );
    if ([woaRes, otherRes].every(Boolean)) {
      setCreating(false);
      props.onChange();
      return true;
    }
    setCreating(false);
    return false;
  };

  const submitToSite = async (params: IServiceConfigItem, site: string) => {
    const { info } = props;
    const formattedParams = {
      ...params,
    };

    const siteServiceItem = info?.allSiteServiceItems?.find(
      (item) => item.site === site
    );
    const isOtherSite = site !== getFirstManageSite();

    if (info) {
      const id = isOtherSite && siteServiceItem ? siteServiceItem.id : info.id;
      return await wrapRequestArea(
        () =>
          updateServiceMethods({
            ...formattedParams,
            id,
          }),
        site
      );
    }
    return await wrapRequestArea(
      () => createServiceMethods(formattedParams),
      site
    );
  };

  // 修改服务配置
  const updateServiceMethods = async (selectRow: IServiceConfigUpdateParam) => {
    const res = await putServiceConfig(selectRow);
    if (res) {
      messageSuccess(getFormatMessage('update_service_success'));
      return true;
    }
    messageError(getFormatMessage('update_service_fail'));
    return false;
  };

  // 获取用户iD
  const getUserLists = async () => {
    const pics = form.getFieldValue('Pic');
    if (pics) {
      return;
    }
    const { woaContactPerson } = getConfig();
    if (woaContactPerson) {
      const listID = props.userLists.find(
        (item) => item.label === `${woaContactPerson}@tencent.com`
      );
      if (listID?.label) {
        form.setFieldsValue({ Pic: [listID.value] });
      }
    }
  };

  useEffect(() => {
    getUserLists();
  }, []);

  // 校验处理
  function handTrimName(e: { target: { value: string } }) {
    const trimmedValue = e.target.value.trim();
    e.target.value = trimmedValue;
  }
  const handleBlur = (
    _rule: any,
    value: string,
    callback: (error?: string) => void
  ) => {
    const regex = /^[a-zA-Z0-9-_ ]+$/g;
    if (!regex.test(value) || /^\d+$/.test(value)) {
      callback(getFormatMessage('name_format_tips'));
    } else {
      callback(); // 校验通过
    }
  };

  const onErrorJson = (error: IJsonEditorError | false) => {
    console.error('error==', error);
    if (error) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  };

  const renderForm = () => {
    const picUserIds: string[] = [];
    formValues?.Pic?.forEach((item) => {
      picUserIds.push(item.split('@')[0]);
    });
    if (isViewMode) {
      return (
        <ConsoleForm className={styles.form}>
          <div className={styles.gridArea}>
            <Form.Item label={getFormatMessage('服务名称')}>
              {formValues?.Name}
            </Form.Item>

            <Form.Item label={getFormatMessage('unique_name')}>
              {formValues?.UniqueName}
            </Form.Item>

            <Form.Item label={getFormatMessage('启用状态配置')}>
              <MultiSiteStatus value={formValues?.SiteStatus} viewOnly />
            </Form.Item>

            <Form.Item label={getFormatMessage('icon_url')}>
              {formValues?.IconURL}
            </Form.Item>

            <Form.Item label={getFormatMessage('业务类型')}>
              {getValueText(
                formValues?.Business,
                getServiceConfigOptions().Business
              )}
            </Form.Item>

            <Form.Item label={getFormatMessage('嵌入类型')}>
              {getValueText(
                formValues?.SupportType,
                getServiceConfigOptions().SupportType
              )}
            </Form.Item>

            <Form.Item label={getFormatMessage('模块名称')}>
              {getValueText(
                formValues?.ParentName,
                getServiceConfigOptions().ParentName
              )}
            </Form.Item>

            <Form.Item label={getFormatMessage('模块内编序')}>
              {formValues?.Sort || ''}
            </Form.Item>

            <Form.Item label={getFormatMessage('release_sort')}>
              {formValues?.ReleaseSort || ''}
            </Form.Item>

            <Form.Item label={getFormatMessage('pic')}>
              {picUserIds.join(',')}
            </Form.Item>

            <Form.Item label={getFormatMessage('subservice_names')}>
              {info?.SubServiceNames}
            </Form.Item>
          </div>

          <div>
            {getNeedManageSites().length > 1 && (
              <Form.Item label={getFormatMessage('服务路径需区分站点')}>
                <Switch disabled checked={formValues?.ServicePathSplitBySite} />
              </Form.Item>
            )}
            {!formValues?.ServicePathSplitBySite && (
              <Form.Item label={getFormatMessage('service_path')}>
                {formValues?.ServicePath}
              </Form.Item>
            )}

            {!!formValues?.ServicePathSplitBySite && (
              <>
                {getNeedManageSites().map((site) => {
                  return (
                    <Form.Item
                      labelCol={{ span: 6 }}
                      wrapperCol={{ span: 18 }}
                      label={`${getSiteText(site)}站点服务路径`}
                    >
                      {formValues[`${site}ServicePath`]}
                    </Form.Item>
                  );
                })}
              </>
            )}
          </div>

          <Form.Item label={getFormatMessage('json_config')}>
            <ReactJsonEditor
              id="create-service-json-editor"
              json={formValues?.JsonConfig}
              height={400}
              width={974}
              mode="preview"
            />
          </Form.Item>
        </ConsoleForm>
      );
    }

    return (
      <ConsoleForm
        className={styles.form}
        form={form}
        name="basic"
        onFinish={onSubmitForm}
        onValuesChange={(_changeValues, values) => {
          setFormValues(values);
        }}
      >
        <div className={styles.gridArea}>
          <Form.Item
            label={getFormatMessage('服务名称')}
            name="Name"
            rules={[
              {
                required: true,
                message: getFormatMessage('please_input_name'),
              },
              { validator: handleBlur },
            ]}
          >
            <Input
              placeholder={getFormatMessage('please_enter_name')}
              onChange={handTrimName}
            />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('unique_name')}
            name="UniqueName"
            rules={[
              {
                required: true,
                message: getFormatMessage('please_enter_unique_name'),
              },
              { validator: handleBlur },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('启用状态配置')}
            name="SiteStatus"
            rules={[
              {
                required: true,
                message: getFormatMessage('please_select_status'),
              },
            ]}
          >
            <MultiSiteStatus />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('icon_url')}
            name="IconURL"
            initialValue="outline"
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('业务类型')}
            name="Business"
            initialValue="currency"
            rules={[
              {
                required: true,
                message: getFormatMessage('please_select_business'),
              },
            ]}
          >
            <CommonSelect
              allowClear
              options={getServiceConfigOptions().Business}
            />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('嵌入类型')}
            name="SupportType"
            initialValue="iframe"
            rules={[
              {
                required: true,
                message: getFormatMessage('please_select_support_type'),
              },
            ]}
          >
            <Select
              allowClear
              style={{ width: 240 }}
              options={getServiceConfigOptions().SupportType}
            />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('模块名称')}
            name="ParentName"
            rules={[
              {
                required: true,
                message: getFormatMessage('please_select_parent_name'),
              },
            ]}
          >
            <CommonSelect
              placeholder={getFormatMessage('select_a_parent_name')}
              allowClear
              options={getServiceConfigOptions().ParentName}
            />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('模块内编序')}
            name="Sort"
            initialValue={1}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('release_sort')}
            name="ReleaseSort"
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('pic')}
            name="Pic"
            rules={[{ required: true }]}
          >
            <CommonSelect
              mode="multiple"
              allowClear={false}
              maxTagCount={2}
              options={props.userLists}
              optionFilterProp="label"
              sortOption
            ></CommonSelect>
          </Form.Item>

          <Form.Item
            label={getFormatMessage('subservice_names')}
            name="SubServiceNames"
          >
            <Input />
          </Form.Item>
        </div>

        <div>
          {getNeedManageSites().length > 1 && (
            <Form.Item
              label={getFormatMessage('服务路径需区分站点')}
              name="ServicePathSplitBySite"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
          {!formValues?.ServicePathSplitBySite && (
            <Form.Item
              label={getFormatMessage('service_path')}
              name="ServicePath"
            >
              <Input style={{ width: 608 }} />
            </Form.Item>
          )}

          {!!formValues?.ServicePathSplitBySite && (
            <>
              {getNeedManageSites().map((site) => {
                return (
                  <Form.Item
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    label={`${getSiteText(site)}站点服务路径`}
                    name={`${site}ServicePath`}
                  >
                    <Input />
                  </Form.Item>
                );
              })}
            </>
          )}
        </div>

        <Form.Item
          label={getFormatMessage('json_config')}
          name="JsonConfig"
          valuePropName="json"
          rules={[
            {
              required: true,
              message: '请输入格式正确的Json配置',
            },
          ]}
        >
          <ReactJsonEditor
            clearWhenWrongFormat
            id="create-service-json-editor"
            onError={onErrorJson}
            height={400}
            width={974}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <MainButton
            style={{ minWidth: 80 }}
            htmlType="submit"
            loading={creating}
          >
            {getFormatMessage('submit')}
          </MainButton>
        </Form.Item>
      </ConsoleForm>
    );
  };

  // 服务弹窗内容
  const renderServiceModal = () => {
    const getModalTitle = () => {
      if (props.dialogMode === 'view') {
        return '查看服务';
      }
      if (props.dialogMode === 'edit') {
        return '更改服务';
      }
      return '创建服务';
    };
    return (
      <ModalWrap
        width="1200px"
        onClose={onClose}
        maskClosable={isViewMode}
        title={<p className="fail-title">{getModalTitle()}</p>}
        className="fail-dialog"
      >
        {renderForm()}
      </ModalWrap>
    );
  };

  return renderServiceModal();
};
